import { useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { FacilityOption, SOURCE_SYSTEMS, sourceSystemLabel } from 'components/inboundReferrals/constants';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import FieldLabel from 'components/shared/form/FieldLabel';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import Tabs, { TabType } from 'components/Tabs';
import { useProfile } from 'context/profile';
import { PERMISSIONS } from 'models/Profile';
import { adminShowClient } from 'services/api/admin/clients';
import { adminGroupsQueryKeys, adminIndexGroups } from 'services/api/admin/groups';
import {
  indexPortalAutomationSettings,
  PortalAutomationSettingOptions,
  portalAutomationSettingQueryKeys,
  RetrievalMode,
  upsertPortalAutomationSetting,
} from 'services/api/portalAutomationSettings';
import {
  deletePortalMapping,
  FACILITY_MAPPING_NAME,
  indexPortalMappings,
  PortalMappingOptions,
  portalMappingQueryKeys,
  upsertPortalMapping,
} from 'services/api/portalMappings';
import {
  AdminPortalScript,
  adminPortalScriptQueryKeys,
  indexAdminPortalScripts,
  PortalScriptPurpose,
  updateAdminPortalScript,
  UpdateAdminPortalScriptInput,
} from 'services/api/portalScripts';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';

// The retrieval-mode ladder: each mode falls back to the one below it.
type RetrievalModeOption = { id: RetrievalMode; name: string; description: string };

const RETRIEVAL_MODE_OPTIONS: RetrievalModeOption[] = [
  { id: 'manual', name: 'Manual', description: 'A person reads the portal and types the details in.' },
  {
    id: 'extension',
    name: 'Extension',
    description: "The Olio browser extension retrieves in the user's browser, with the manual form as fallback.",
  },
];

// Retrieval scripts pull one referral's records; discovery scripts sweep the portal
// for new referral activity. Rows group by purpose so an admin renaming or archiving
// can tell them apart when both exist for the same source system.
const PURPOSE_ORDER: PortalScriptPurpose[] = ['retrieval', 'discovery'];
const PURPOSE_LABELS: Record<PortalScriptPurpose, string> = {
  retrieval: 'Retrieval',
  discovery: 'Discovery',
};

// The TS type says every row carries a known purpose, but that's a compile-time assertion
// over whatever the api actually served — a row with a missing or unrecognized purpose
// (legacy payload, future third purpose) must still be manageable here, not silently
// dropped from the only surface that can rename/archive it.
export function purposeLabel(purpose: string | undefined): string {
  return PURPOSE_LABELS[purpose as PortalScriptPurpose] ?? 'Other';
}

type ScriptGrouping = { key: string; header: string; group: AdminPortalScript[] };

function groupedByPurpose(scripts: AdminPortalScript[]): ScriptGrouping[] {
  const known: ScriptGrouping[] = PURPOSE_ORDER.map((purpose) => ({
    key: purpose,
    header: PURPOSE_LABELS[purpose],
    group: scripts.filter((s) => s.purpose === purpose),
  }));
  const other = scripts.filter((s) => !PURPOSE_ORDER.includes(s.purpose));
  return [...known, { key: 'other', header: 'Other', group: other }].filter(({ group }) => group.length > 0);
}

type SystemForm = { retrievalMode: RetrievalMode };
type Forms = Record<string, SystemForm>;

function deepEqual(a: unknown, b: unknown) {
  return JSON.stringify(a) === JSON.stringify(b);
}

// The API rides the admin_client permissions (Admin::PortalAutomationSettingPolicy /
// Admin::PortalScriptPolicy): adminClientView to read, adminClientEdit to write.
// View-only users get the page with every field disabled, per admin convention.
export default function PortalAutomationSettings() {
  const { id } = useParams<{ id: string }>();
  const { profile } = useProfile();
  const { addToast } = useToastActions();
  const queryClient = useQueryClient();
  const disabled = !profile.has(PERMISSIONS.adminClientEdit);

  const clientQuery = useQuery({
    queryKey: ['adminClient', id],
    queryFn: ({ signal }) => adminShowClient(id!, signal),
    enabled: !!id,
  });
  const settingsQuery = useQuery({
    queryKey: portalAutomationSettingQueryKeys.list(id!),
    queryFn: ({ signal }) => indexPortalAutomationSettings(id!, signal),
    enabled: !!id,
  });
  const scriptsQuery = useQuery({
    queryKey: adminPortalScriptQueryKeys.list(id!),
    queryFn: ({ signal }) => indexAdminPortalScripts(id!, signal),
    enabled: !!id,
  });
  const mappingsQuery = useQuery({
    queryKey: portalMappingQueryKeys.list(id!),
    queryFn: ({ signal }) => indexPortalMappings(id!, signal),
    enabled: !!id,
  });
  // The facility choices a mapping can point at — the client's provider groups.
  const providerGroupsQuery = useQuery({
    queryKey: [...adminGroupsQueryKeys.index, 'providers', id],
    queryFn: ({ signal }) => adminIndexGroups({ clientId: id!, isProvider: true, pageSize: 200 }, signal),
    enabled: !!id,
  });

  const serverForms = useRef<Forms>({});
  const [forms, setForms] = useState<Forms>({});
  const [selectedTabId, setSelectedTabId] = useState<string>(SOURCE_SYSTEMS[0]);

  useEffect(() => {
    if (!settingsQuery.data) return;

    const built: Forms = {};
    for (const sourceSystem of SOURCE_SYSTEMS) {
      const setting = settingsQuery.data.data.find((s) => s.sourceSystem === sourceSystem);
      built[sourceSystem] = {
        retrievalMode: setting?.retrievalMode ?? 'manual',
      };
    }
    serverForms.current = built;
    setForms(built);
  }, [settingsQuery.data]);

  const isDirty = !deepEqual(forms, serverForms.current);

  const { mutate: save, isPending } = useMutation({
    mutationFn: async (updated: Forms) => {
      const dirty = Object.entries(updated).filter(([sys, form]) => !deepEqual(form, serverForms.current[sys]));

      await Promise.all(
        dirty.map(([sourceSystem, form]) =>
          upsertPortalAutomationSetting({
            clientId: id!,
            sourceSystem,
            retrievalMode: form.retrievalMode,
          })
        )
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portalAutomationSettingQueryKeys.list(id!) });
      addToast({ text: 'Portal automation settings saved.' });
    },
    onError: (error) => {
      const message =
        isAxiosError(error) && error.response?.status === 403 && error.response.data?.message
          ? error.response.data.message
          : 'There was an error saving the portal automation settings.';
      addToast({ text: message });
    },
  });

  const handleDiscard = () => setForms({ ...serverForms.current });

  const tabs: TabType[] = SOURCE_SYSTEMS.map((sourceSystem) => ({
    label: sourceSystemLabel(sourceSystem),
    value: sourceSystem,
  }));
  const selectedTab = tabs.find((t) => t.value === selectedTabId);
  const selectedForm = forms[selectedTabId];

  if (settingsQuery.isPending || clientQuery.isPending) {
    return <CircularProgress style={{ margin: '48px auto', display: 'block' }} />;
  }

  return (
    <PageContainer>
      <Header>
        <HeaderTop>
          <TitleArea>
            <Title>Portal Automation</Title>
            <Subtitle>{clientQuery.data?.name}</Subtitle>
          </TitleArea>
          {!disabled && (
            <ButtonGroup>
              <Button variant='ghost' onClick={handleDiscard} disabled={!isDirty || isPending}>
                Discard Changes
              </Button>
              <Button data-testid='portal-automation-save' onClick={() => save(forms)} disabled={!isDirty || isPending}>
                Save Changes
              </Button>
            </ButtonGroup>
          )}
        </HeaderTop>
        {tabs.length > 1 && (
          <TabsWrapper data-testid='portal-automation-tabs'>
            <Tabs tabs={tabs} selectedTab={selectedTab} onTabClick={(tab) => setSelectedTabId(tab.value)} />
          </TabsWrapper>
        )}
      </Header>

      <Content>
        {settingsQuery.isError && <ErrorText>Portal automation settings failed to load.</ErrorText>}
        {selectedForm && id && (
          <SourceSystemSettings
            key={selectedTabId}
            clientId={id}
            sourceSystem={selectedTabId}
            setting={settingsQuery.data?.data.find((s) => s.sourceSystem === selectedTabId)}
            form={selectedForm}
            onChange={(updated) => setForms((prev) => ({ ...prev, [selectedTabId]: updated }))}
            scripts={(scriptsQuery.data ?? []).filter((s) => s.sourceSystem === selectedTabId)}
            mappings={(mappingsQuery.data?.data ?? []).filter(
              (m) => m.sourceSystem === selectedTabId && m.name === FACILITY_MAPPING_NAME
            )}
            facilityOptions={(providerGroupsQuery.data?.data ?? []).map((group) => ({
              id: group.id!,
              name: group.name ?? group.id!,
            }))}
            disabled={disabled}
          />
        )}
      </Content>
    </PageContainer>
  );
}

type SourceSystemProps = {
  clientId: string;
  sourceSystem: string;
  setting?: PortalAutomationSettingOptions;
  form: SystemForm;
  onChange: (form: SystemForm) => void;
  scripts: AdminPortalScript[];
  mappings: PortalMappingOptions[];
  facilityOptions: FacilityOption[];
  disabled: boolean;
};

function SourceSystemSettings({
  clientId,
  sourceSystem,
  setting,
  form,
  onChange,
  scripts,
  mappings,
  facilityOptions,
  disabled,
}: SourceSystemProps) {
  const label = sourceSystemLabel(sourceSystem);
  const mode = RETRIEVAL_MODE_OPTIONS.find((o) => o.id === form.retrievalMode) ?? RETRIEVAL_MODE_OPTIONS[0];

  return (
    <SystemSection data-testid={`portal-automation-${sourceSystem}`}>
      <InputGroup title='Retrieval Mode'>
        <Select
          inputId={`retrieval-mode-${sourceSystem}`}
          options={RETRIEVAL_MODE_OPTIONS}
          value={mode}
          onChange={(v) => v && onChange({ ...form, retrievalMode: v.id })}
          getOptionValue={(o) => o!.id}
          getOptionLabel={(o) => o!.name}
          isDisabled={disabled}
          formatOptionLabel={(o, meta) => {
            if (!o) return null;
            if (meta?.context === 'value') return o.name;
            return (
              <div>
                <div>{o.name}</div>
                <div style={{ fontSize: '0.9em', color: colors.black50 }}>{o.description}</div>
              </div>
            );
          }}
        />
      </InputGroup>

      <ScriptsBlock data-testid={`portal-credentials-${sourceSystem}`}>
        <FieldLabel>Headless sign-in</FieldLabel>
        <HeadlessCredentialStatus setting={setting} label={label} />
      </ScriptsBlock>

      <ScriptsBlock data-testid={`portal-scripts-${sourceSystem}`}>
        <FieldLabel>Recorded scripts</FieldLabel>
        {scripts.length === 0 && <MutedNote>No scripts recorded for {label} yet.</MutedNote>}
        {groupedByPurpose(scripts).map(({ key, header, group }) => (
          <ScriptGroup key={key} data-testid={`portal-scripts-${sourceSystem}-${key}`}>
            <GroupHeader>{header}</GroupHeader>
            {group.map((script) => (
              <ScriptRow key={script.id} clientId={clientId} script={script} disabled={disabled} />
            ))}
          </ScriptGroup>
        ))}
      </ScriptsBlock>

      <MappingsBlock
        clientId={clientId}
        sourceSystem={sourceSystem}
        label={label}
        mappings={mappings}
        facilityOptions={facilityOptions}
        disabled={disabled}
      />
    </SystemSection>
  );
}

type MappingsBlockProps = {
  clientId: string;
  sourceSystem: string;
  label: string;
  mappings: PortalMappingOptions[];
  facilityOptions: FacilityOption[];
  disabled: boolean;
};

// The tab's facility mappings: how this portal's facility strings resolve to Olio
// facilities. Rows are usually born from the workflow's mapping step (a person
// resolving a halted run); this surface is for review and correction.
function MappingsBlock({ clientId, sourceSystem, label, mappings, facilityOptions, disabled }: MappingsBlockProps) {
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();
  const [externalValue, setExternalValue] = useState('');
  const [facility, setFacility] = useState<FacilityOption | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: portalMappingQueryKeys.list(clientId) });

  const upsert = useMutation({
    mutationFn: () =>
      upsertPortalMapping({
        clientId,
        sourceSystem,
        name: FACILITY_MAPPING_NAME,
        externalValue: externalValue.trim(),
        mappedValue: facility!.id,
      }),
    onSuccess: () => {
      refresh();
      setExternalValue('');
      setFacility(null);
    },
    onError: () => addToast({ text: 'There was an error saving the mapping.' }),
  });

  const remove = useMutation({
    mutationFn: (mappingId: string) => deletePortalMapping(mappingId, clientId),
    onSuccess: refresh,
    onError: () => addToast({ text: 'There was an error removing the mapping.' }),
  });

  const addReady = externalValue.trim().length > 0 && !!facility && !upsert.isPending;

  return (
    <MappingsSection data-testid={`portal-mappings-${sourceSystem}`}>
      <FieldLabel>Facility mappings</FieldLabel>
      <MutedNote>How facility names in {label} map to your Olio facilities.</MutedNote>
      {mappings.length === 0 && <MutedNote>No mappings for {label} yet.</MutedNote>}
      {mappings.map((mapping) => (
        <MappingRow key={mapping.id} data-testid={`portal-mapping-${mapping.id}`}>
          <MappingExternal>{mapping.externalValue}</MappingExternal>
          <MappingArrow>→</MappingArrow>
          <MappingTarget>{mapping.mappedLabel ?? mapping.mappedValue}</MappingTarget>
          {!disabled && (
            <MappingDelete
              type='button'
              aria-label='Remove mapping'
              onClick={() => remove.mutate(mapping.id)}
              disabled={remove.isPending}
              data-testid={`portal-mapping-delete-${mapping.id}`}>
              ×
            </MappingDelete>
          )}
        </MappingRow>
      ))}
      {!disabled && (
        <MappingAddRow>
          <Input
            placeholder={`Facility name in ${label}`}
            value={externalValue}
            onChange={(e) => setExternalValue(e.target.value)}
            data-testid={`portal-mapping-external-${sourceSystem}`}
          />
          <MappingSelectCell>
            <Select<FacilityOption>
              inputId={`portal-mapping-facility-${sourceSystem}`}
              placeholder='Olio facility'
              options={facilityOptions}
              value={facility}
              onChange={setFacility}
              menuPortalTarget={document.body}
              getOptionValue={(option) => option.id}
              getOptionLabel={(option) => option.name}
            />
          </MappingSelectCell>
          <Button
            size='small'
            onClick={() => upsert.mutate()}
            disabled={!addReady}
            data-testid={`portal-mapping-add-${sourceSystem}`}>
            Add
          </Button>
        </MappingAddRow>
      )}
    </MappingsSection>
  );
}

// Read-only mirror of the integration platform's credential state (OLIO-2887's
// status line; revoke arrives with its backend, OLIO-2907). The mirror is a
// cache — the integration platform re-checks the real secret at run time.
function HeadlessCredentialStatus({ setting, label }: { setting?: PortalAutomationSettingOptions; label: string }) {
  const status = setting?.headlessCredentialsStatus;
  if (!status) {
    return <MutedNote data-testid='headless-credentials-none'>No sign-in saved for automatic retrieval.</MutedNote>;
  }

  const username = setting?.headlessCredentialsUsername ?? 'unknown user';
  const savedAt = setting?.headlessCredentialsSavedAt;
  const verifiedAt = setting?.headlessCredentialsVerifiedAt;
  const saved = savedAt ? ` saved ${new Date(savedAt).toLocaleDateString()}` : '';

  if (status === 'invalid') {
    return (
      <InvalidNote data-testid='headless-credentials-invalid'>
        {username} —{saved}, but {label} rejected it. It needs to be saved again from the retrieval card.
      </InvalidNote>
    );
  }
  return (
    <MutedNote data-testid='headless-credentials-status'>
      {username} —{saved}, {verifiedAt ? `verified ${new Date(verifiedAt).toLocaleDateString()}` : 'not yet verified'}
    </MutedNote>
  );
}

type ScriptRowProps = {
  clientId: string;
  script: AdminPortalScript;
  disabled: boolean;
};

// One recorded script: rename inline or archive/restore. Bodies never reach the
// admin surface — a script's steps change only by re-recording in the extension.
function ScriptRow({ clientId, script, disabled }: ScriptRowProps) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(script.name);

  const queryClient = useQueryClient();
  const { addToast } = useToastActions();

  const mutation = useMutation({
    mutationFn: (patch: Omit<UpdateAdminPortalScriptInput, 'id'>) =>
      updateAdminPortalScript({ id: script.id, ...patch }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminPortalScriptQueryKeys.list(clientId) });
      setEditing(false);
    },
    onError: () => addToast({ text: 'There was an error updating the script.' }),
  });

  const archived = !!script.archivedAt;
  return (
    <ScriptRowContainer data-testid={`portal-script-${script.id}`} $archived={archived}>
      <ScriptMain>
        {editing ? (
          <ScriptRenameRow>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid={`script-name-${script.id}`} />
            <Button
              size='small'
              onClick={() => name.trim() && mutation.mutate({ name: name.trim() })}
              disabled={mutation.isPending}>
              Save
            </Button>
            <Button size='small' variant='ghost' onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </ScriptRenameRow>
        ) : (
          <>
            <ScriptName>{script.name}</ScriptName>
            <PurposeBadge $purpose={script.purpose} data-testid={`script-purpose-${script.id}`}>
              {purposeLabel(script.purpose)}
            </PurposeBadge>
            {archived && <ArchivedBadge>Archived</ArchivedBadge>}
          </>
        )}
        <ScriptMeta>
          {script.user.email} · updated {new Date(script.updatedAt).toLocaleDateString()}
        </ScriptMeta>
      </ScriptMain>
      {!editing && !disabled && (
        <ScriptActions>
          <ScriptActionButton onClick={() => setEditing(true)} disabled={mutation.isPending}>
            Rename
          </ScriptActionButton>
          <ScriptActionButton
            onClick={() => mutation.mutate({ archived: !archived })}
            disabled={mutation.isPending}
            data-testid={`script-archive-${script.id}`}>
            {archived ? 'Restore' : 'Archive'}
          </ScriptActionButton>
        </ScriptActions>
      )}
    </ScriptRowContainer>
  );
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const Header = styled.div`
  flex: 0 0 auto;
  background: white;
  border-bottom: 1px solid ${colors.black10};
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px 24px 16px;
`;

const TitleArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  color: ${colors.black100};
`;

const Subtitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  margin: 0;
  color: ${colors.blue100};
`;

const TabsWrapper = styled.div`
  border-top: 1px solid ${colors.black10};
  padding: 0 24px;
`;

const Content = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 24px;
`;

const SystemSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 560px;
`;

const InvalidNote = styled.p`
  font-size: 13px;
  color: ${colors.red100};
  margin: 4px 0 0;
`;

const MutedNote = styled.p`
  font-size: 12px;
  color: ${colors.black50};
  margin: 0;
`;

const ScriptsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
`;

const MappingsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
`;

const MappingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border: 1px solid ${colors.black10};
  border-radius: 6px;
`;

const MappingExternal = styled.span`
  font-size: 13px;
  font-weight: 600;
  overflow-wrap: anywhere;
`;

const MappingArrow = styled.span`
  color: ${colors.black50};
  font-size: 12px;
`;

const MappingTarget = styled.span`
  font-size: 13px;
  overflow-wrap: anywhere;
`;

const MappingDelete = styled.button`
  margin-left: auto;
  background: none;
  border: none;
  padding: 0 4px;
  cursor: pointer;
  color: ${colors.black50};
  font-size: 16px;
  line-height: 1;
`;

const MappingAddRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MappingSelectCell = styled.div`
  flex: 0 0 220px;
`;

const ScriptRowContainer = styled.div<{ $archived: boolean }>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 10px;
  border: 1px solid ${colors.black10};
  border-radius: 6px;
  opacity: ${({ $archived }) => ($archived ? 0.6 : 1)};
`;

const ScriptMain = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const ScriptName = styled.span`
  font-size: 13px;
  font-weight: 600;
  overflow-wrap: anywhere;
`;

const ScriptMeta = styled.span`
  flex-basis: 100%;
  font-size: 11px;
  color: ${colors.black50};
`;

const ScriptRenameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ScriptGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GroupHeader = styled.span`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${colors.black50};
  margin-top: 4px;
`;

// Neutral for anything that isn't a known purpose — the row still renders (see
// groupedByPurpose), the badge just doesn't pretend to know what it is.
const PurposeBadge = styled.span<{ $purpose: string }>`
  font-size: 11px;
  color: ${({ $purpose }) =>
    $purpose === 'discovery' ? colors.purple100 : $purpose === 'retrieval' ? colors.blue100 : colors.black50};
  border: 1px solid
    ${({ $purpose }) =>
      $purpose === 'discovery' ? colors.purple50 : $purpose === 'retrieval' ? colors.blue50 : colors.black25};
  border-radius: 10px;
  padding: 1px 8px;
`;

const ArchivedBadge = styled.span`
  font-size: 11px;
  color: ${colors.black50};
  border: 1px solid ${colors.black25};
  border-radius: 10px;
  padding: 1px 8px;
`;

const ScriptActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
`;

const ScriptActionButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 12px;
  color: ${colors.blue100};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }

  &:disabled {
    color: ${colors.black25};
    cursor: default;
  }
`;

const ErrorText = styled.p`
  font-size: 14px;
  color: ${colors.red100};
  margin: 0;
`;
